import { css } from "@emotion/react";
import BookIcon from "@mui/icons-material/MenuBook";
import FrjLogoSmall from "@assets/frj-logo-small.png";

import { chapterSlide, chipPop, shelfPlace } from "./animations";
import type { Source, SourceGroup, UpsellPopoverState } from "./utils";
import { getSourceLabel, getSourceUrl, isBookSource } from "./utils";
import { Link } from "react-router";

type Props = {
  sources: Source[];
  visibleSourceCount: number;
  publicationsBySlug?: Record<string, unknown>;
  getSourceCoverUrl: (s: Source) => string | undefined;
  setUpsellPopover: (p: UpsellPopoverState | null) => void;
};

export const SourceCards = ({
  sources,
  visibleSourceCount,
  publicationsBySlug,
  getSourceCoverUrl,
  setUpsellPopover,
}: Props) => {
  if (sources.length === 0) return null;

  const groups: SourceGroup[] = [];
  const groupMap = new Map<string, SourceGroup>();

  sources.forEach((s, i) => {
    if (i >= visibleSourceCount) return;
    if (isBookSource(s)) {
      const groupKey = s.book_id!;
      let group = groupMap.get(groupKey);
      if (!group) {
        const slug = s.fragment_id?.split("-ch:")[0];
        const owned = slug ? publicationsBySlug?.[slug] !== undefined : false;
        group = {
          key: groupKey,
          label: getSourceLabel(s),
          coverUrl: getSourceCoverUrl(s),
          isBook: true,
          isOwned: owned,
          purchaseUrl: s.purchase_url,
          items: [],
        };
        groupMap.set(groupKey, group);
        groups.push(group);
      } else if (!group.isOwned) {
        // If any edition is owned, mark entire group as owned
        const slug = s.fragment_id?.split("-ch:")[0];
        if (slug && publicationsBySlug?.[slug]) {
          group.isOwned = true;
        }
      }
      group.items.push({ source: s, globalIndex: i });
    } else {
      groups.push({
        key: `article-${i}`,
        label: getSourceLabel(s),
        isBook: false,
        isOwned: true,
        items: [{ source: s, globalIndex: i }],
      });
    }
  });

  const bookGroups = groups.filter((g) => g.isBook);
  const articleGroups = groups.filter((g) => !g.isBook);

  return (
    <>
      {bookGroups.length > 0 && (
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            align-items: stretch;
            gap: 8px;
            margin-bottom: 8px;
          `}
        >
          {bookGroups.map((group) => (
            <div
              key={group.key}
              css={({ colours }) => css`
                display: flex;
                gap: 10px;
                background: ${colours.primary.light._5};
                border: 1px solid ${colours.primary.light._30};
                border-radius: 8px;
                padding: 8px 12px;
                animation: ${shelfPlace} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)
                  both;
                min-width: 0;
                align-self: stretch;
              `}
            >
              <div
                css={css`
                  flex-shrink: 0;
                  width: 48px;
                  display: flex;
                  align-items: flex-start;
                `}
              >
                {group.coverUrl ? (
                  <img
                    src={group.coverUrl}
                    alt=""
                    css={css`
                      width: 48px;
                      height: 64px;
                      border-radius: 3px;
                      object-fit: cover;
                      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
                    `}
                  />
                ) : (
                  <BookIcon
                    css={({ colours }) => css`
                      width: 48px;
                      height: 64px;
                      color: ${colours.primary.light._70};
                    `}
                  />
                )}
              </div>

              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                  min-width: 0;
                `}
              >
                <span
                  css={({ colours }) => css`
                    font-size: 11px;
                    font-weight: 700;
                    color: ${colours.primary.mid.dark20};
                    line-height: 1.3;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {group.label}
                </span>
                {group.items.map(({ source: s, globalIndex }) => {
                  const { url, target, rel } = group.isOwned
                    ? getSourceUrl(s, publicationsBySlug)
                    : {};
                  return (
                    <Link
                      to={url || "#"}
                      target={target}
                      rel={rel}
                      key={`source-${s.index}`}
                      role="link"
                      onClick={(e) => {
                        if (!group.isOwned && group.purchaseUrl) {
                          setUpsellPopover({
                            anchorEl: e.currentTarget as HTMLElement,
                            bookTitle: group.label,
                            purchaseUrl: group.purchaseUrl,
                          });
                        }
                      }}
                      css={({ colours }) => css`
                        display: flex;
                        align-items: baseline;
                        gap: 5px;
                        text-decoration: none;
                        cursor: pointer;
                        border-radius: 4px;
                        padding: 1px 4px 1px 0;
                        line-height: 1.4;
                        animation: ${chapterSlide} 0.3s ease-out both;

                        &:hover {
                          background: ${colours.primary.light._10};
                        }
                      `}
                    >
                      <span
                        css={({ colours }) => css`
                          font-size: 10px;
                          font-weight: 700;
                          color: ${colours.primary.mid.dark20};
                          min-width: 14px;
                          text-align: right;
                          flex-shrink: 0;
                        `}
                      >
                        {globalIndex + 1}
                      </span>
                      <span
                        css={({ colours }) => css`
                          font-size: 12px;
                          font-weight: 400;
                          color: ${colours.primary.dark._20};
                          overflow: hidden;
                          text-overflow: ellipsis;
                          white-space: nowrap;
                        `}
                      >
                        {s.heading}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {articleGroups.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-bottom: 12px;
          `}
        >
          {articleGroups.map((group) => {
            const { source: s, globalIndex } = group.items[0];
            const { url, target, rel } = getSourceUrl(s, publicationsBySlug);
            return (
              <Link
                key={group.key}
                to={url || "#"}
                target={target}
                rel={rel}
                role={url ? "link" : undefined}
                css={({ colours }) => css`
                  display: inline-flex;
                  align-items: center;
                  text-decoration: none;
                  gap: 5px;
                  background: ${colours.primary.light._5};
                  border: 1px solid ${colours.primary.light._30};
                  border-radius: 16px;
                  padding: 4px 10px;
                  max-width: 320px;
                  cursor: ${url ? "pointer" : "default"};
                  animation: ${chipPop} 0.3s
                    cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

                  &:hover {
                    background: ${colours.primary.light._10};
                  }
                `}
              >
                <span
                  css={({ colours }) => css`
                    font-size: 10px;
                    font-weight: 700;
                    color: ${colours.primary.mid.dark20};
                    flex-shrink: 0;
                  `}
                >
                  {globalIndex + 1}
                </span>
                <img
                  src={FrjLogoSmall}
                  alt="FRJ"
                  css={css`
                    height: 22px;
                    width: auto;
                    flex-shrink: 0;
                  `}
                />
                <span
                  css={({ colours }) => css`
                    font-size: 12px;
                    font-weight: 400;
                    color: ${colours.primary.dark._20};
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  {group.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
};
